/**
 * magneticMonopoleTouchPipeline — the monopole is a particle you can
 * grab. The pointer moves the monopole to its location (with smooth
 * easing), and clicks flip the polarity (north ↔ south). The field
 * lines and Dirac string all follow.
 *
 * Based on magneticMonopolePipeline.ts — same field equations, same
 * audio reactivity, but the monopole's position and polarity are
 * driven by the pointer and clicks.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MagneticMonopoleTouchPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  uPointer: WebGLUniformLocation | null;
  uPointerActive: WebGLUniformLocation | null;
  uPolarity: WebGLUniformLocation | null;
  bands: Float32Array;
  /** Smoothed pointer in canvas pixels (y-up, after the embed
   *  forwarder has flipped y). */
  smoothX: number;
  smoothY: number;
  /** Pointer-active ramp (0..1). */
  _active: number;
  /** Polarity: +1 or -1. Toggled by clicks. */
  polarity: number;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
// Pointer in canvas pixel space (y-up after the embed's y-flip).
// When uPointerActive is 0, the monopole stays at the center.
uniform vec2 uPointer;
uniform float uPointerActive;
uniform float uPolarity; // +1 (north) or -1 (south)

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);

  // Monopole position: blend between center (when no pointer)
  // and the pointer position. uPointerActive drives the blend so
  // the field smoothly transitions between "sitting at the center"
  // and "stuck to the pointer".
  vec2 pPos = (uPointer - 0.5 * uResolution) / uResolution.y;
  // The pointer comes in y-down (browser). The shader uses y-up.
  // We trust the embed's forwarder to have done the flip already
  // (it sends the y-up value), so no further flip is needed here.
  // Smooth the trail: when the pointer is moving fast, the
  // monopole lags slightly behind.
  vec2 mpPos = mix(vec2(0.0), pPos, clamp(uPointerActive, 0.0, 1.0));
  // No additional lag — the field is more "stuck to the cursor" feel.
  vec2 toMp = p - mpPos;
  float mpD = length(toMp);

  vec3 bg = mix(vec3(0.04, 0.04, 0.10), vec3(0.01, 0.01, 0.03), smoothstep(0.0, 0.7, r));
  vec3 col = bg;

  // Inner glow — color depends on polarity.
  // North: warm (yellow/red). South: cool (cyan/blue).
  vec3 coreWarm = vec3(1.0, 0.95, 0.7);
  vec3 coreCool = vec3(0.6, 0.85, 0.95);
  vec3 coreCol = mix(coreCool, coreWarm, smoothstep(-1.0, 1.0, uPolarity));
  col += coreCol * exp(-mpD * 50.0) * 1.5;
  col += mix(vec3(0.4, 0.55, 0.95), vec3(0.95, 0.5, 0.3), smoothstep(-1.0, 1.0, uPolarity)) * exp(-mpD * 12.0) * 0.6;
  col += mix(vec3(0.3, 0.4, 0.7), vec3(0.7, 0.3, 0.4), smoothstep(-1.0, 1.0, uPolarity)) * exp(-mpD * 4.0) * 0.3;

  // Concentric field-strength rings, centered on the monopole.
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    float ringR = 0.08 + fi * 0.05;
    float strength = 1.0 / (ringR + 0.05);
    float ringD = abs(mpD - ringR);
    float ringMask = exp(-ringD * 80.0);
    vec3 ringCol = mix(vec3(0.95, 0.4, 0.3), vec3(0.6, 0.5, 0.95), fi / 7.0);
    col += ringCol * ringMask * strength * 1.2;
  }

  // The Dirac string — a dashed line from the monopole outward.
  // Direction: depends on time + audio + a touch of the pointer
  // direction (so it leans toward where you're moving).
  vec2 baseDir = vec2(cos(uTime * 0.3 + bass), sin(uTime * 0.3 + bass));
  // Lean: blend with the pointer's recent direction.
  vec2 ptrDir = length(pPos) > 0.001 ? normalize(pPos) : vec2(1.0, 0.0);
  vec2 stringDir = normalize(mix(baseDir, ptrDir, uPointerActive * 0.4));
  float minD = 1e9;
  for (int k = 0; k < 30; k++) {
    float fk = float(k) / 29.0;
    vec2 pt = mpPos + stringDir * fk * 0.8;
    float d = length(p - pt);
    minD = min(minD, d);
  }
  float dashPattern = 0.5 + 0.5 * sin(dot(p - mpPos, stringDir) * 30.0 - uTime * 4.0);
  float stringMask = (1.0 - smoothstep(0.0, 0.004, minD)) * dashPattern;
  col += mix(vec3(0.4, 0.65, 0.95), vec3(0.6, 0.85, 0.95), smoothstep(-1.0, 1.0, uPolarity))
       * stringMask * 0.6;

  // A "drag trail" — when the monopole is moving, leave a faint
  // smear of glow along its recent path. (We approximate this by
  // a softer falloff off-center: any point near the pointer path
  // gets a small additive glow.)
  float trail = exp(-length(toMp) * 8.0) * uPointerActive * 0.4;
  col += coreCol * trail;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}`;

export function computeMagneticMonopoleTouchBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeMagneticMonopoleTouchRealtimeBands(
  freqData: Uint8Array, out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) { sum += freqData[k]; count++; }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMagneticMonopoleTouchPipeline(gl: WebGL2RenderingContext): MagneticMonopoleTouchPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution:    gl.getUniformLocation(program, "uResolution"),
    uTime:          gl.getUniformLocation(program, "uTime"),
    uBass:          gl.getUniformLocation(program, "uBass"),
    uMid:           gl.getUniformLocation(program, "uMid"),
    uHigh:          gl.getUniformLocation(program, "uHigh"),
    uPointer:       gl.getUniformLocation(program, "uPointer"),
    uPointerActive: gl.getUniformLocation(program, "uPointerActive"),
    uPolarity:      gl.getUniformLocation(program, "uPolarity"),
    bands: new Float32Array(9),
    smoothX: 0, smoothY: 0,
    _active: 0,
    polarity: 1,
  };
}

export function drawMagneticMonopoleTouch(
  gl: WebGL2RenderingContext,
  pipe: MagneticMonopoleTouchPipeline,
  width: number, height: number, t: number,
): void {
  gl.useProgram(pipe.program);
  gl.uniform2f(pipe.uResolution!, width, height);
  gl.uniform1f(pipe.uTime!, t);
  gl.uniform3f(pipe.uBass!, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(pipe.uMid!, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(pipe.uHigh!, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.uniform2f(pipe.uPointer!, pipe.smoothX, pipe.smoothY);
  gl.uniform1f(pipe.uPointerActive!, pipe._active);
  gl.uniform1f(pipe.uPolarity!, pipe.polarity);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
